import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { AdminLayout } from "@/components/shared/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Building2, Edit, Trash2, Plus, Search, ExternalLink, Sparkles, FileText } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token") || ""}` });

const BLANK = {
  city: "", state: "", state_code: "", slug: "",
  population: "", county: "",
  intro_paragraph: "", why_open_here: "",
  neighborhoods: [], nearby_metros: [],
  has_incentive: true, active: true, sort_order: 100,
  meta_title: "", meta_description: "",
};

export default function AdminCities({ embedded = false }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [progress, setProgress] = useState(null);

  const loadProgress = async () => {
    try {
      const { data } = await axios.get(
        `${API_URL}/admin/cities/dealer-recruitment-blogs/progress`,
        { headers: authHeader() },
      );
      setProgress(data);
    } catch (e) { /* silent — progress card just won't render */ }
  };

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/admin/cities`, { headers: authHeader() });
      setList(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load cities");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); loadProgress(); }, []);
  // Poll the progress endpoint while a background batch is running so the
  // admin sees the counter tick up in near real-time. Only polls when there
  // is still pending work.
  useEffect(() => {
    if (!progress || progress.pending === 0) return;
    const id = setInterval(loadProgress, 6000);
    return () => clearInterval(id);
  }, [progress]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((c) => `${c.city} ${c.state} ${c.state_code} ${c.slug}`.toLowerCase().includes(q));
  }, [list, search]);

  const openNew = () => setEditing({ ...BLANK });
  const openEdit = (c) => setEditing({ ...c, neighborhoods: c.neighborhoods || [], nearby_metros: c.nearby_metros || [] });

  const save = async () => {
    if (!editing.city || !editing.state || !editing.state_code) {
      toast.error("City, State, and State Code are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editing,
        state_code: editing.state_code.toUpperCase(),
        sort_order: parseInt(editing.sort_order, 10) || 100,
      };
      if (editing.id) {
        const { data } = await axios.put(`${API_URL}/admin/cities/${editing.id}`, payload, { headers: { ...authHeader(), "Content-Type": "application/json" } });
        toast.success(`Saved ${data.city}, ${data.state_code}`);
      } else {
        const { data } = await axios.post(`${API_URL}/admin/cities`, payload, { headers: { ...authHeader(), "Content-Type": "application/json" } });
        toast.success(`Created ${data.city}, ${data.state_code}`);
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const doDelete = async (c) => {
    try {
      await axios.delete(`${API_URL}/admin/cities/${c.id}`, { headers: authHeader() });
      toast.success(`Deleted ${c.city}, ${c.state_code}`);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const [generatingBlogs, setGeneratingBlogs] = useState(false);
  const generateAllDealerBlogs = async () => {
    const force = window.confirm(
      `Generate dealer-recruitment blog posts for every active city (${list.length} cities)?\n\n` +
      "Click OK to FORCE-REGENERATE: deletes existing auto-generated dealer-recruitment posts and creates fresh ones with the latest title variants and content.\n\n" +
      "Click Cancel to skip force mode — but you'll be asked whether to continue in a moment."
    );
    if (!force) {
      const cont = window.confirm(
        "Continue in non-force mode? Cities that already have a dealer-recruitment blog will be SKIPPED (existing posts stay untouched). Click OK to proceed, Cancel to abort."
      );
      if (!cont) return;
    }
    setGeneratingBlogs(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/admin/cities/generate-all-dealer-blogs`,
        { force },
        { headers: { ...authHeader(), "Content-Type": "application/json" } }
      );
      toast.success(
        `Dealer-recruitment blogs · ${data.queued} queued · ${data.already_have} skipped` +
        (data.regen_stale ? ` · ${data.regen_stale} regen'd for new metros` : "") +
        (data.wiped ? ` · ${data.wiped} old posts wiped` : "")
      );
      loadProgress();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk blog generation failed");
    } finally { setGeneratingBlogs(false); }
  };

  return (
    <ConditionalLayout embedded={embedded}>
    <div className={embedded ? "space-y-6" : "space-y-6 p-6 max-w-7xl mx-auto"} data-testid="admin-cities">
      {progress && (
        <DealerBlogProgressCard progress={progress} onRefresh={loadProgress} />
      )}
      <Card className="border-slate-200">
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-navy font-heading flex items-center gap-2">
            <Building2 className="w-5 h-5 text-teal" /> Dedicated City Pages
            <span className="text-xs font-normal text-slate-500 ml-2">{list.length} total</span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button
              onClick={generateAllDealerBlogs}
              disabled={generatingBlogs || list.length === 0}
              variant="outline"
              className="border-teal text-teal hover:bg-teal hover:text-white"
              data-testid="cities-generate-dealer-blogs-btn"
              title="Auto-publish a &quot;Looking for a Total Wireless Dealer in [City]&quot; blog post for every city that doesn't have one yet"
            >
              <FileText className="w-4 h-4 mr-1" /> {generatingBlogs ? "Generating…" : "Generate Dealer Recruitment Blogs"}
            </Button>
            <Button onClick={openNew} className="bg-teal hover:bg-teal-dark text-white" data-testid="cities-new-btn">
              <Plus className="w-4 h-4 mr-1" /> New City
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by city, state, or slug…"
                className="pl-9"
                data-testid="cities-search"
              />
            </div>
          </div>
          {loading ? (
            <p className="text-slate-500 text-center py-8 font-body">Loading…</p>
          ) : filtered.length === 0 ? (
            <p className="text-slate-500 text-center py-10 font-body">No cities yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="cities-table">
                <thead className="border-b border-slate-200">
                  <tr className="text-xs uppercase tracking-wider text-slate-500">
                    <th className="text-left py-2 font-semibold">City, State</th>
                    <th className="text-left py-2 font-semibold">Slug</th>
                    <th className="text-center py-2 font-semibold">Incentive</th>
                    <th className="text-center py-2 font-semibold">Active</th>
                    <th className="text-center py-2 font-semibold">Order</th>
                    <th className="text-right py-2 font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => (
                    <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-2.5 font-medium text-navy">{c.city}, {c.state_code}</td>
                      <td className="py-2.5 font-mono text-xs text-slate-600">/cities/{c.slug}</td>
                      <td className="py-2.5 text-center">
                        {c.has_incentive ? (
                          <span className="inline-flex items-center gap-1 text-teal text-xs font-semibold"><Sparkles className="w-3 h-3" />Yes</span>
                        ) : <span className="text-slate-400 text-xs">—</span>}
                      </td>
                      <td className="py-2.5 text-center">
                        {c.active ? <span className="text-green-700 text-xs font-semibold">Active</span> : <span className="text-slate-400 text-xs">Off</span>}
                      </td>
                      <td className="py-2.5 text-center text-xs text-slate-500">{c.sort_order}</td>
                      <td className="py-2.5 text-right whitespace-nowrap">
                        <a href={`/cities/${c.slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center text-slate-500 hover:text-teal mr-2" title="View public page">
                          <ExternalLink className="w-4 h-4" />
                        </a>
                        <button onClick={() => openEdit(c)} className="text-slate-500 hover:text-teal mr-2" data-testid={`city-edit-${c.slug}`}>
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => setConfirmDelete(c)} className="text-slate-500 hover:text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit / Create Dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="city-edit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? `Edit ${editing.city}, ${editing.state_code}` : "New City Page"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid sm:grid-cols-3 gap-3">
                <div className="sm:col-span-2">
                  <Label>City *</Label>
                  <Input value={editing.city} onChange={(e) => setEditing({ ...editing, city: e.target.value })} data-testid="city-form-city" />
                </div>
                <div>
                  <Label>State Code *</Label>
                  <Input value={editing.state_code} onChange={(e) => setEditing({ ...editing, state_code: e.target.value.toUpperCase() })} maxLength={2} data-testid="city-form-state-code" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>State (full) *</Label>
                  <Input value={editing.state} onChange={(e) => setEditing({ ...editing, state: e.target.value })} data-testid="city-form-state" />
                </div>
                <div>
                  <Label>Slug (auto if blank)</Label>
                  <Input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} placeholder="e.g. detroit-mi" data-testid="city-form-slug" className="font-mono text-sm" />
                </div>
              </div>

              <div>
                <Label>Population / Market Profile</Label>
                <Input value={editing.population} onChange={(e) => setEditing({ ...editing, population: e.target.value })} placeholder="e.g. 4.3M metro" />
              </div>

              <div>
                <Label>Intro Paragraph (auto-built if blank)</Label>
                <Textarea rows={3} value={editing.intro_paragraph} onChange={(e) => setEditing({ ...editing, intro_paragraph: e.target.value })} />
              </div>

              <div>
                <Label>Why Open Here (body paragraph)</Label>
                <Textarea rows={4} value={editing.why_open_here} onChange={(e) => setEditing({ ...editing, why_open_here: e.target.value })} />
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Neighborhoods (one per line)</Label>
                  <Textarea
                    rows={4}
                    value={(editing.neighborhoods || []).join("\n")}
                    onChange={(e) => setEditing({ ...editing, neighborhoods: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                  />
                </div>
                <div>
                  <Label>Nearby Metros (one per line)</Label>
                  <Textarea
                    rows={4}
                    value={(editing.nearby_metros || []).join("\n")}
                    onChange={(e) => setEditing({ ...editing, nearby_metros: e.target.value.split("\n").map((s) => s.trim()).filter(Boolean) })}
                  />
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Meta Title (auto if blank)</Label>
                  <Input value={editing.meta_title} onChange={(e) => setEditing({ ...editing, meta_title: e.target.value })} />
                </div>
                <div>
                  <Label>Sort Order</Label>
                  <Input type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: e.target.value })} />
                </div>
              </div>

              <div>
                <Label>Meta Description (auto if blank)</Label>
                <Textarea rows={2} value={editing.meta_description} onChange={(e) => setEditing({ ...editing, meta_description: e.target.value })} />
              </div>

              <div className="grid sm:grid-cols-2 gap-3 pt-2 border-t border-slate-100">
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div>
                    <Label className="font-medium">Opening Incentive Available</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Show "incentives available" badge on the page.</p>
                  </div>
                  <Switch checked={!!editing.has_incentive} onCheckedChange={(v) => setEditing({ ...editing, has_incentive: v })} data-testid="city-form-incentive-toggle" />
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-slate-50 border border-slate-200">
                  <div>
                    <Label className="font-medium">Active</Label>
                    <p className="text-xs text-slate-500 mt-0.5">Off = hide from public + sitemap.</p>
                  </div>
                  <Switch checked={!!editing.active} onCheckedChange={(v) => setEditing({ ...editing, active: v })} />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-teal hover:bg-teal-dark text-white" data-testid="city-form-save">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete city page?</DialogTitle>
          </DialogHeader>
          {confirmDelete && (
            <p className="text-slate-600 font-body">
              This will permanently remove the page <code className="bg-slate-100 px-1 py-0.5 rounded text-xs">/cities/{confirmDelete.slug}</code> from your site and sitemap. This cannot be undone.
            </p>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)}>Cancel</Button>
            <Button onClick={() => doDelete(confirmDelete)} className="bg-red-600 hover:bg-red-700 text-white">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </ConditionalLayout>
  );
}

function ConditionalLayout({ embedded, children }) {
  if (embedded) return <>{children}</>;
  return <AdminLayout>{children}</AdminLayout>;
}

// Dealer-recruitment blog progress. Shows expected/published/pending, a
// visual progress bar, per-variant counts, and city rollup. Auto-polls
// every 6s while `pending > 0` so the admin sees the batch tick up live.
function DealerBlogProgressCard({ progress, onRefresh }) {
  const pct = progress.expected_total > 0
    ? Math.min(100, Math.round((progress.published / progress.expected_total) * 100))
    : 0;
  const isDone = progress.pending === 0 && progress.expected_total > 0;
  const variantLabels = {
    own_tw_store: "Own TW Store",
    cell_phone_store: "Cell Phone Store",
    repair_store: "Repair Store",
    buy_phone_store: "Buy a Store",
  };
  const lastGen = progress.last_generated_at
    ? new Date(progress.last_generated_at).toLocaleString()
    : "—";
  return (
    <Card
      className={`border-slate-200 ${isDone ? "bg-emerald-50/40" : "bg-white"}`}
      data-testid="dealer-blog-progress-card"
    >
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-4">
          <CardTitle className="text-navy font-heading text-base flex items-center gap-2">
            <FileText className="w-4 h-4 text-teal" />
            Dealer Recruitment Blog Progress
            {isDone && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-medium">
                Complete
              </span>
            )}
          </CardTitle>
          <button
            type="button"
            onClick={onRefresh}
            className="text-xs text-slate-500 hover:text-navy underline underline-offset-2"
            data-testid="dealer-blog-progress-refresh"
          >
            Refresh
          </button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <ProgressStat
            label="Published"
            value={progress.published}
            sub={`of ${progress.expected_total}`}
            testid="progress-published"
            tone="teal"
          />
          <ProgressStat
            label="Pending"
            value={progress.pending}
            sub={progress.pending > 0 ? "still generating…" : "all done"}
            testid="progress-pending"
            tone={progress.pending > 0 ? "amber" : "slate"}
          />
          <ProgressStat
            label="Cities Complete"
            value={progress.cities_complete}
            sub={`of ${progress.total_cities}`}
            testid="progress-cities-complete"
            tone="emerald"
          />
          <ProgressStat
            label="Cities Missing"
            value={progress.cities_missing}
            sub={progress.cities_partial ? `${progress.cities_partial} partial` : "0 partial"}
            testid="progress-cities-missing"
            tone="slate"
          />
        </div>
        <div>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-1">
            <span className="font-body">Overall</span>
            <span className="font-medium text-navy" data-testid="progress-percent">{pct}%</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2 overflow-hidden">
            <div
              className={`h-full transition-all duration-500 ${isDone ? "bg-emerald-500" : "bg-teal"}`}
              style={{ width: `${pct}%` }}
              data-testid="progress-bar"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          {Object.keys(variantLabels).map((key) => {
            const n = progress.by_variant?.[key] || 0;
            const total = progress.total_cities;
            const done = total > 0 && n >= total;
            return (
              <div
                key={key}
                className={`px-2 py-1.5 rounded border ${done ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}
                data-testid={`variant-progress-${key}`}
              >
                <div className="text-[11px] text-slate-500 font-body">{variantLabels[key]}</div>
                <div className={`text-sm font-semibold ${done ? "text-emerald-700" : "text-navy"}`}>
                  {n}/{total}
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-[11px] text-slate-500 font-body">
          Last generated: {lastGen}
          {progress.pending > 0 && (
            <span className="ml-2 text-amber-600">· auto-refreshing every 6s while pending &gt; 0</span>
          )}
          {progress.queue && (progress.queue.processing > 0 || progress.queue.pending > 0 || progress.queue.failed > 0) && (
            <span className="ml-2 inline-flex items-center gap-2" data-testid="progress-queue-strip">
              <span className="text-slate-400">·</span>
              <span title="Items claimed by the worker right now">
                <span className="text-teal font-semibold">{progress.queue.processing}</span> processing
              </span>
              <span title="Items waiting for the worker">
                <span className="text-amber-600 font-semibold">{progress.queue.pending}</span> queued
              </span>
              {progress.queue.failed > 0 && (
                <span className="text-rose-600" title="Items that hit the retry cap">
                  <span className="font-semibold">{progress.queue.failed}</span> failed
                </span>
              )}
            </span>
          )}
        </p>
      </CardContent>
    </Card>
  );
}

function ProgressStat({ label, value, sub, testid, tone = "slate" }) {
  const tones = {
    teal: "text-teal",
    amber: "text-amber-600",
    emerald: "text-emerald-600",
    slate: "text-slate-700",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-2.5" data-testid={testid}>
      <div className="text-[11px] text-slate-500 font-body uppercase tracking-wide">{label}</div>
      <div className={`text-2xl font-heading font-bold ${tones[tone] || tones.slate}`}>{value}</div>
      <div className="text-[11px] text-slate-500 font-body">{sub}</div>
    </div>
  );
}
